import { message } from "antd";
import axios, { AxiosRequestConfig } from "axios";
import { apiV1Endpoint } from "../api/v1";
import { clearSession, getAccessToken, getLoginResponse, getRefreshToken, saveSession } from "./session";

// TEMP: set false to restore cookie auth
const USE_BEARER_AUTH_TEMP = true;

const refreshClient = axios.create(USE_BEARER_AUTH_TEMP ? undefined : { withCredentials: true });

if (!USE_BEARER_AUTH_TEMP) {
  // Cookies carry auth; never attach Bearer on the default client
  delete axios.defaults.headers.common["Authorization"];
  axios.defaults.withCredentials = true;
}

// ── Queue pattern: ensures only ONE refresh call fires for concurrent 401s ─────
type QueueEntry = { resolve: (token?: string) => void; reject: (err: unknown) => void };

let isRefreshing = false;
let failedQueue: QueueEntry[] = [];

const processQueue = (error: unknown, token?: string | null) => {
  failedQueue.forEach(({ resolve, reject }) => (error ? reject(error) : resolve(token ?? undefined)));
  failedQueue = [];
};
// ─────────────────────────────────────────────────────────────────────────────

const fetchNewTokenBearer = async (refreshToken: string) => {
  const { data } = await refreshClient.post(
    `${apiV1Endpoint}/refresh-token`,
    { refreshToken },
    { headers: { appname: "institutionalLandlordPortal" } },
  );
  return data.result as { token: string; refreshToken: string };
};

const fetchNewTokenCookie = async () => {
  await refreshClient.post(`${apiV1Endpoint}/refresh-token`, null, {
    headers: { appname: "institutionalLandlordPortal" },
  });
};

const applyToken = (token: string) => {
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
};

const handleSessionExpired = () => {
  clearSession();
  message.info({ content: "Your session has expired. Please log in again.", key: "session-expired" });
  setTimeout(() => {
    window.location.href = "/login";
  }, 500);
};

const isGoogleTranslateRequest = (url?: string) => url?.includes("translation.googleapis.com");

// ── Request interceptor ────────────────────────────────────────────────────────

axios.interceptors.request.use((config) => {
  if (isGoogleTranslateRequest(config.url)) {
    if (!USE_BEARER_AUTH_TEMP) {
      // Keep Google Translate cookieless / tokenless
      config.withCredentials = false;
    }
    return config;
  }

  if (USE_BEARER_AUTH_TEMP) {
    const token = getAccessToken();
    if (token) config.headers["Authorization"] = `Bearer ${token}`;
  } else {
    config.withCredentials = true;
  }

  config.headers["appname"] = "institutionalLandlordPortal";
  return config;
});

// ── Response interceptor ───────────────────────────────────────────────────────

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    if (status === 403) message.error("You are not authorized to perform this action");
    if (status === 500) message.error("Internal server error");

    if (status !== 401) return Promise.reject(error);

    // Avoid retry loops on the refresh call itself
    if (originalRequest.url?.includes("/refresh-token") || originalRequest._retry) {
      handleSessionExpired();
      return Promise.reject(error);
    }

    // A refresh is already in progress — queue this request and wait
    if (isRefreshing) {
      return new Promise<string | undefined>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        if (USE_BEARER_AUTH_TEMP && token) {
          originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${token}` };
        }
        return axios(originalRequest);
      });
    }

    if (USE_BEARER_AUTH_TEMP) {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        handleSessionExpired();
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const result = await fetchNewTokenBearer(refreshToken);
        saveSession({ ...getLoginResponse()!, ...result });
        applyToken(result.token);
        processQueue(null, result.token);
        originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${result.token}` };
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        handleSessionExpired();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      await fetchNewTokenCookie();
      processQueue(null);
      return axios(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);
      handleSessionExpired();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default axios;
